"use client";

import { useEffect, useRef, useState } from "react";

type AnimatedCounterProps = {
    from?: number;
    to: number;
    duration?: number;
    className?: string;
    desktopFractionDigits?: number;
    mobileFractionDigits?: number;
};

const easeInOutCubic = (t: number) =>
    t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;

const formatValue = (value: number, fractionDigits: number) =>
    new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: fractionDigits,
    }).format(value);

const AnimatedCounter = ({
    from = 0,
    to,
    duration = 1500,
    className,
    desktopFractionDigits = 0,
    mobileFractionDigits = desktopFractionDigits,
}: AnimatedCounterProps) => {
    const ref = useRef<HTMLSpanElement | null>(null);
    const hasAnimated = useRef(false);
    const animationFrameRef = useRef<number | null>(null);

    const [value, setValue] = useState(to);

    useEffect(() => {
        const el = ref.current;

        hasAnimated.current = false;

        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry?.isIntersecting || hasAnimated.current) return;

                hasAnimated.current = true;
                setValue(from);

                const start = performance.now();

                const animate = (now: number) => {
                    const progress = Math.min((now - start) / duration, 1);
                    const eased = easeInOutCubic(progress);
                    const current = from + (to - from) * eased;

                    setValue(current);

                    if (progress < 1) {
                        animationFrameRef.current =
                        requestAnimationFrame(animate);
                    } else {
                        animationFrameRef.current = null;
                        setValue(to);
                    }
                };

                animationFrameRef.current = requestAnimationFrame(animate);
            },
            { threshold: 0.4 },
        );

        observer.observe(el);

        return () => {
            observer.disconnect();

            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [from, to, duration]);

    return (
        <span ref={ref} className={className}>
            <span className="md:hidden">
                {formatValue(value, mobileFractionDigits)}
            </span>

            <span className="hidden md:inline">
                {formatValue(value, desktopFractionDigits)}
            </span>
        </span>
    );
};

export default AnimatedCounter;